import { useContext, useEffect, useState } from "react";
import { getPlans } from "../API/PlansApi";
import AuthContext from "../Context/AuthProvider";
import {
  convertUTCToTimeZoneDateTimeString,
} from "../Utility/DateTimeUtility";
import { getDisplayValue } from "../Utility/Utility";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Grid2,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  Card,
  CardContent,
  CardActions,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import DateRangeDialog from "../Appointments/ChangeDates";
import { useSnackbar } from "../Utility/UserMessageContext";
import { formatAge } from "../Utility/Utility";
import MenuBar from "../common/MenuBar";
import { MoreVert } from "@mui/icons-material";
import dayjs from "dayjs";
import { DateRangeOptions } from "../Utility/Constants";

function PlanList({
  onViewDetails,
  onAddNewPlan,
  onEditPlan,
  onViewPatientDetails,
}) {
  const { auth } = useContext(AuthContext);
  const [allPlans, setAllPlans] = useState([]);
  const [showSpinner, setShowSpinner] = useState(false);
  const [showQueue, setShowQueue] = useState();
  const { showSnackbar } = useSnackbar();
  // Theme for responsive design
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const [openDateRangeDialog, setOpenDateRangeDialog] = useState(false);

  const handleMenuOpen = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setCurrentRow(row); // Keep track of the selected row
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setCurrentRow(null);
  };

  const [filters, setFilters] = useState({
      from: dayjs(new Date()),
      to: dayjs(new Date()),
      selectedDateRangeOption: DateRangeOptions.Today,
    });

  useEffect(() => {
    const fetchData = async () => {
      setShowSpinner(true);

      try {
        if(!filters){
          return;
        }
        const result = await getPlans(auth, {
          from: filters?.from,
          to: filters?.to,
          facilityId: auth?.selectedFacility?.id,
        });
        if (result.isSuccess) {
          setAllPlans(result?.data);
        }
      } catch (error) {
        console.error("Error loading plans:", error);
        showSnackbar(
          "An unexpected error occurred while loading plans.",
          "error",
        );
      } finally {
        setShowSpinner(false);
      }
    };

    fetchData();
  }, [auth, filters]);

  const [pageSize, setPageSize] = useState(5);

  const columns = [
    {
      field: "displayId",
      headerName: "ID",
      width: 180,
      renderCell: (params) => (
        <Box>
          <span
            style={{
              textDecoration: "underline",
              cursor: "pointer",
            }}
            onClick={() => onViewDetails(params.row)}
          >
            {params.row.displayId}
          </span>
          <Tooltip title="Actions" arrow>
            <IconButton
              size="small"
              onClick={(event) => handleMenuOpen(event, params.row)}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "name",
      headerName: "Plan Name",
      width: 180,
      renderCell: (params) => params.row.name || "",
    },
    {
      field: "patientName",
      type: "text",
      headerName: "Patient",
      width: 200,
      renderCell: (params) => {
        const visit = params?.row?.visits?.[0]; // First visit object
        const patientPrefix = visit?.patientPrefix || "";
        const firstName = visit?.patientFirstName || "";
        const lastName = visit?.patientLastName || "";
        const gender = visit?.patientGender || "";
        const age = visit?.patientAgeInYears || "";
        const phone = visit?.patientMobileNo || "";
        const countryCode = params?.row?.countryCodeForPhone || "";
        const email = params?.row?.email || "";

        return (
          <div
            style={{
              lineHeight: 1.3,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div>
              <span
                style={{
                  fontWeight: "bold",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={() => {
                  const patientData = {
                    id: visit?.patientId,
                    firstName: visit?.patientFirstName,
                    lastName: visit?.patientLastName,
                    prefix: visit?.patientPrefix,
                    gender: visit?.patientGender,
                    ageInYears: visit?.patientAgeInYears,
                    mobileNo: visit?.patientMobileNo,
                    emailId: visit?.patientEmailId,
                  };
                  onViewPatientDetails && onViewPatientDetails(patientData);
                }}
              >
                {patientPrefix} {firstName} {lastName}
              </span>
              <span style={{ fontWeight: "normal" }}> ({gender})</span>
            </div>
            <span style={{ color: "#555", fontSize: "0.85em" }}>
              {`${countryCode} ${phone} (${formatAge(age)})`}
            </span>
          </div>
        );
      },
    },
    {
      field: "physicianName",
      type: "text",
      headerName: "Physician",
      width: 150,
      renderCell: (params) => params?.row?.physicianName,
    },

    {
      field: "visit",
      headerName: "Sessions",
      width: 90,
      renderCell: (params) => {
        const total = params?.row?.visits?.length;
        const completed = params?.row?.visits?.reduce((count, visit) => {
          return visit?.visitStatus === "Completed" ? count + 1 : count;
        }, 0);

        return ` ${completed} / ${total}`;
      },
    },
    {
      field: "startDate",
      headerName: "Start Date",
      width: 220,
      renderCell: (params) =>
        convertUTCToTimeZoneDateTimeString(
          params?.row?.startDate,
          auth?.selectedFacility?.timeZone,
        ),
    },
    {
      field: "endDate",
      headerName: "Last Date",
      width: 220,
      renderCell: (params) =>
        convertUTCToTimeZoneDateTimeString(
          params?.row?.endDate,
          auth?.selectedFacility?.timeZone,
        ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => getDisplayValue(params?.row?.status),
    },
  ];

  // Card view for small screens
  const renderCardView = () => {
    return (
      <Grid2 sx={{ px: 1 }}>
        {showSpinner ? (
          <Typography variant="body1" align="center" py={3}>
            Loading...
          </Typography>
        ) : allPlans.length === 0 ? (
          <Typography variant="body1" align="center" py={3}>
            There are no plans to display. Change the dates or create a new
            plan.
          </Typography>
        ) : (
          allPlans.map((plan) => (
            <Card
              item
              xs={12}
              key={plan.id}
              variant="outlined"
              sx={{ mb: 2, borderRadius: 2 }}
            >
              <CardContent>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                  onClick={() => onViewDetails(plan)}
                >
                  {plan.displayId}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {plan.name}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {(() => {
                    const total = plan?.visits?.length || 0;
                    const completed = plan?.visits?.reduce(
                      (count, visit) =>
                        visit?.visitStatus === "Completed" ? count + 1 : count,
                      0,
                    );
                    return `${
                      plan.physicianName || "Physician Name"
                    } ||   Session: ${completed} / ${total}`;
                  })()}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2">
                  Patient:
                  {(() => {
                    const visit = plan?.visits?.[0];
                    const prefix = visit?.patientPrefix || "";
                    const firstName = visit?.patientFirstName || "";
                    const lastName = visit?.patientLastName || "";
                    const gender = visit?.patientGender || "";
                    return ` ${prefix} ${firstName} ${lastName} (${gender})`;
                  })()}
                </Typography>
                <Typography variant="body2">
                  Age: {formatAge(plan?.visits?.[0]?.patientAgeInYears)}
                </Typography>
                <Typography variant="body2">
                  Contact: {plan?.visits?.[0]?.patientMobileNo || "-"}
                </Typography>
                <Typography variant="body2">
                  Start Date:{" "}
                  {convertUTCToTimeZoneDateTimeString(
                    plan?.startDate,
                    auth?.selectedFacility?.timeZone,
                  )}
                  ,
                </Typography>
                <Typography variant="body2">
                  End Date:{" "}
                  {convertUTCToTimeZoneDateTimeString(
                    plan?.endDate,
                    auth?.selectedFacility?.timeZone,
                  )}
                  ,
                </Typography>
                <Typography variant="body2">
                  Status: {getDisplayValue(plan?.status) || "-"}
                </Typography>
                <CardActions sx={{ justifyContent: "flex-end" }}>
                  <Tooltip title="View Details">
                    <IconButton
                      onClick={() => onViewDetails(plan)}
                      size="small"
                    >
                      <EventNoteOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </CardContent>
            </Card>
          ))
        )}
      </Grid2>
    );
  };

  return (
    <>
      <Grid2 container>
        {/* Plan Actions and Date Display */}
        <Grid2 container size={12}>
          <Grid2 size={12}>
            <MenuBar
              showChangeDate={true}
              onChangeDate={() => setOpenDateRangeDialog(true)}
              showNew={true}
              onNew={onAddNewPlan}
              newLabel="New Plan"
              newTooltip="New Plan"
              onShowQueue={() => setShowQueue(!showQueue)}
              disabled={false}
            />
          </Grid2>
          {/* Date Range Display */}
          <Grid2 size={12} sx={{ px: 1, py: 1 }}>
            <Typography variant="body1">
              Showing plans from {filters?.from?.format("DD/MM/YYYY")} to{" "}
              {filters?.to?.format("DD/MM/YYYY")}
            </Typography>
          </Grid2>
        </Grid2>
        {(!isSmallScreen || !showQueue) && (
          <Box
            sx={{
              width: isSmallScreen ? "100%" : showQueue ? "80%" : "100%",
              transition: "width 0.3s ease",
              paddingX: "5px",
              height: isSmallScreen ? "auto" : 600,
              maxHeight: "calc(100vh - 180px)",
              overflowY: "auto",
            }}
          >
            {isSmallScreen ? (
              renderCardView()
            ) : (
              <DataGrid
                localeText={{
                  noRowsLabel:
                    "There are no plans to display. Change the dates or create a new plan.",
                }}
                loading={showSpinner}
                slotProps={{
                  loadingOverlay: {
                    variant: "skeleton",
                    noRowsVariant: "skeleton",
                  },
                }}
                initialState={{
                  density: "compact",
                  pagination: {
                    paginationModel: { pageSize: 5 }, // Default page size
                  },
                }}
                sx={{
                  width: "100%",
                  "& .sticky-column": {
                    position: "sticky",
                    right: 0, // Keep it on the right side
                    backgroundColor: "white", // To make sure it has a solid background
                    zIndex: 100, // Ensure it stays on top of other columns
                  },
                  "& .MuiTablePagination-toolbar": {
                    minHeight: "unset",
                    alignItems: "center",
                    paddingTop: "0 !important",
                    paddingBottom: "0 !important",
                  },
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                    {
                      margin: 0,
                    },
                }}
                rows={allPlans} //filteredPlans
                columns={columns}
                pageSizeOptions={[5, 10, 15]}
                rowsPerPageOptions={[5, 10, 15]}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              />
            )}

            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
            >
              {currentRow && (
                <>
                  <MenuItem onClick={() => onEditPlan(currentRow)}>
                    Edit Plan
                  </MenuItem>
                </>
              )}
            </Menu>
            {/* </Box> */}
          </Box>
        )}
        {!isSmallScreen && (
          <Box
            sx={{
              display: "flex",
              width: showQueue ? "20%" : "0", // Control the width based on open state
              transition: "width 1s ease", // Smooth transition for the width
              justifyContent: "flex-end", // Position collapsible on the right
              overflow: "hidden", // Hide content that goes outside the box
            }}
          ></Box>
        )}
        <Grid2 sx={{ maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}>
          {/* For small screens, show queue as a full width section if enabled */}
        </Grid2>
      </Grid2>
      <div class="container-fluid">
        <div class="row justify-content-center align-items-center mt-4">
          <DateRangeDialog
            key="plans"
            title="Show Plans For"
            filters={filters}
            setFilters={setFilters}
            openState={openDateRangeDialog}
            setOpenState={setOpenDateRangeDialog}
            // onApply={handleApplyDateRange}
          ></DateRangeDialog>
        </div>
      </div>
    </>
  );
}

export default PlanList;
